import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import type { IResourceType, ISimpleResourceType } from '@/components/common';
import type { IResourceDatabaseFolder, IResourceFolderType } from '@/api/types';
import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { ResourceTypeId } from '@/store/enums';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { useAppSelector, useFolderModal, useResourceNavigation } from '@/hooks';
import { ButtonAdd, ResourcesBreadcrumb, ResourcesSimpleTableContainer } from '@/components/common';
import { getSelectedFolderId, itemTranslationMap } from '@/helpers';
import { ResourceDatabasesContainer } from '../ResourceDatabasesContainer';
import { ResourceFoldersTable } from '../ResourceFoldersTable';
import { FolderModalType } from '@/hooks/enums';
import { combineFoldersToShow } from '@/store/combiners/resourcesCombiners';

const resourceCategoryRouteSelector = createSelector(
  selectFoldersList,
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, selectedFolder) => ({
    foldersList,
    foldersToShow: combineFoldersToShow(foldersList, selectedFolder),
  }),
);

const ResourceCategoryRoute = (): React.ReactElement => {
  const { selectedFolder } = useResourceNavigation();
  const { foldersToShow } = useAppSelector(state => resourceCategoryRouteSelector(state, selectedFolder));
  const { t } = useTranslation();
  const { handleOpenModal: handleOpenFolderModal } = useFolderModal();

  if (selectedFolder?.folderId === ResourceTypeId.database) {
    return (
      <>
        <ResourcesBreadcrumb />
        <ResourcesSimpleTableContainer
          items={foldersToShow}
          tableTitle={t(itemTranslationMap[ResourceTypeId[selectedFolder.folderId] as IResourceType])}
          tableComponentSlot={
            <ResourceDatabasesContainer
              items={foldersToShow as IResourceDatabaseFolder[]}
              clientId={selectedFolder.clientId}
            />
          }
          tableTitleActionSlot={
            // TODO: onClick functionality is to be added in the future
            <ButtonAdd size="s" onClick={() => ''}>{t`resources.content.createDatabase`}</ButtonAdd>
          }
        />
      </>
    );
  }

  return (
    <>
      <ResourcesBreadcrumb />
      <ResourcesSimpleTableContainer
        items={foldersToShow}
        tableTitle={t(itemTranslationMap[ResourceTypeId[selectedFolder.folderId] as IResourceType])}
        tableComponentSlot={
          <ResourceFoldersTable items={foldersToShow as Exclude<IResourceFolderType, IResourceDatabaseFolder>[]} />
        }
        tableTitleActionSlot={
          <ButtonAdd
            size="s"
            onClick={() =>
              handleOpenFolderModal({
                modalType: FolderModalType.new,
                data: {
                  folderType: ResourceTypeId[selectedFolder.folderId] as ISimpleResourceType,
                  parentFolderId: getSelectedFolderId(selectedFolder),
                  clientId: selectedFolder.clientId,
                },
              })
            }
          >{t`resources.content.addFolder`}</ButtonAdd>
        }
      />
    </>
  );
};

export default ResourceCategoryRoute;
